import React, { useCallback, useEffect, useRef, useState } from 'react';
import NpmReactQuill, { Quill } from 'react-quill-new';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import 'quill-table-up/index.css';
// If using the default customSelect option. You need to import this css
import 'quill-table-up/table-creator.css';
import { ErrorMessage, Label, Modal, Tabs, Toast } from '../index';
import styles from './index.module.scss';
import { handleFileUploadChange } from '@utils/file-uploader-utils';
import TableUp, {
  defaultCustomSelect,
  TableAlign,
  TableMenuContextmenu,
  TableResizeBox,
  TableResizeScale,
  TableSelection,
  TableVirtualScrollbar,
} from 'quill-table-up';
import 'react-quill-new/dist/quill.snow.css';
import cx from 'clsx';
import { useSelector } from 'react-redux';
import { createFileFromString } from '../../../utils/file-uploader-utils';
import { strings } from '@constants/strings';
import VideoForm from './video-form';
import { Spin } from 'antd';
import tenantConfig from 'data/customizer/tenant-config';

Quill.register(
  {
    [`modules/${TableUp.moduleName}`]: TableUp,
  },
  true
);

const ReactQuill = ({
  placeholder = '',
  value = '',
  onChange = () => {},
  onBlur = () => {},
  readOnly = false,
  theme,
  className,
  label = '',
  errorMsg = '',
  editorHeight,
  modules,
  required = false,
  loading = false,
  setLoading = () => {},
  serverDescription,
  setHasDescriptionUpdated,
}) => {
  const quillRef = useRef(null);
  const [videoModal, setVideoModal] = useState(false);
  const videoFormRef = useRef(null);

  const tenant = useSelector((state) => state?.tenantData?.tenant);
  const addProductConfig = tenantConfig?.product?.add_product;

  const filterMultimedia = (e) => {
    let newHTML = e;
    if (!addProductConfig?.allow_image_in_description) newHTML = newHTML?.replace(/<img[^>]*>/gi, '');
    if (!addProductConfig?.allow_link_in_description) newHTML = newHTML?.replace(/<a[^>]*>/gi, '');
    if (!addProductConfig?.allow_video_in_description)
      newHTML = newHTML?.replace(/<video[^>]*>/gi, '')?.replace(/<audio[^>]*>/gi, '');
    return newHTML;
  };

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.setAttribute('multiple', true);
    input.click();
    input.onchange = async () => {
      if (input !== null && input.files !== null) {
        setLoading(true);
        const files = input.files;
        let fileUploadPromises = [];
        for (let i = 0; i < files?.length; i++) {
          fileUploadPromises?.push(
            handleFileUploadChange({
              file: files?.[i],
              fileList: [files?.[i]],
              associationKey: 'description_images',
              attachmentType: 'generic',
            })
          );
        }
        const updatedFileArrays = await Promise.all(fileUploadPromises);
        const updatedFiles = updatedFileArrays?.flatMap((file) => file);
        if (updatedFiles?.length) {
          updatedFiles?.forEach((image) => {
            const quill = quillRef.current;
            if (quill) {
              const range = quill.getEditorSelection();
              range && quill.getEditor().insertEmbed(range.index, 'image', `${image?.preview}#${image?.id}`);
            }
          });
        }
        setLoading(false);
      }
    };
  }, []);

  const videoHandler = useCallback(() => {
    const quill = quillRef?.current;
    if (quill) {
      setVideoModal({ range: quill.getEditorSelection() });
    }
  }, []);

  const addVideo = (url = '') => {
    const quill = quillRef?.current;
    if (quill) {
      const editor = quill.getEditor();
      if (videoModal?.range) {
        editor.insertEmbed(videoModal?.range?.index, 'video', url);
        editor.setSelection(videoModal?.range?.index + 1);
      } else {
        editor.insertEmbed(0, 'video', url);
      }
    }
    setVideoModal(false);
  };

  const defaultModules = {
    toolbar: {
      handlers: {
        image: imageHandler,
        video: videoHandler,
      },
      container: [
        [{ header: ['2', '3', false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [
          ...(addProductConfig?.allow_link_in_description ? ['link'] : []),
          ...(addProductConfig?.allow_image_in_description ? ['image'] : []),
          ...(addProductConfig?.allow_video_in_description ? ['video'] : []),
        ],
        ['clean'],
        [
          // use picker to enable the customSelect option
          { [TableUp.toolName]: [] },
        ],
      ],
    },
    clipboard: {
      matchVisual: false,
    },
    [TableUp.moduleName]: {
      scrollbar: TableVirtualScrollbar,
      align: TableAlign,
      resize: TableResizeBox,
      resizeScale: TableResizeScale,
      customSelect: defaultCustomSelect,
      selection: TableSelection,
      selectionOptions: {
        tableMenu: TableMenuContextmenu,
      },
    },
  };

  function normalizeHtmlForComparison(htmlString) {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    const elements = doc.body.querySelectorAll('*');

    elements.forEach((el) => {
      Array.from(el.attributes).forEach((attr) => {
        if (attr.name === 'class' || attr.name === 'id' || attr.name.startsWith('data-')) {
          el.removeAttribute(attr.name);
        }
      });

      if (el.hasAttribute('style')) {
        const style = el.getAttribute('style');
        const styles = style
          .split(';')
          .map((rule) => rule.trim())
          .filter(Boolean);

        const colorRule = styles.find((rule) => {
          const [name] = rule.split(':').map((s) => s.trim().toLowerCase());
          return name === 'color';
        });

        if (colorRule) {
          el.setAttribute('style', colorRule);
        } else {
          el.removeAttribute('style');
        }
      }
      // Trim text nodes directly under this element
      el.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          node.textContent = node.textContent.trim();
        }
      });
    });

    return doc.body.innerHTML.trim();
  }

  // to track description changes especially in case of edit product to avoid triggering moderation
  useEffect(() => {
    if (quillRef?.current && quillRef.current.getEditor) {
      const editor = quillRef.current.getEditor();
      if (editor) {
        const normalizedServerHtml = normalizeHtmlForComparison(serverDescription);
        const normalizedCurrentHtml = normalizeHtmlForComparison(value);
        const isSame = normalizedServerHtml === normalizedCurrentHtml;
        setHasDescriptionUpdated(!isSame);
      }
    }
  }, [value, serverDescription]);

  const handlePaste = async (e, files = []) => {
    const oldUrls = files?.map((file) => file?.dataUrl);
    const filesForUpload = files?.map((obj) => obj?.file);
    let newUrlPromises = [];
    for (const file of filesForUpload) {
      newUrlPromises?.push(
        (async () => {
          const updatedFiles = await handleFileUploadChange({
            file,
            fileList: [file],
            associationKey: 'description_images',
            attachmentType: 'generic',
          });
          if (updatedFiles?.length) {
            const image = updatedFiles?.[0];
            return `${image?.preview}#${image?.id}`;
          }
        })()
      );
    }
    try {
      const newUrls = await Promise.all(newUrlPromises);
      let newHTML = e;
      oldUrls?.forEach((url, index) => {
        newHTML = newHTML.replace(url, newUrls?.[index]);
      });
      return newHTML;
    } catch (err) {
      Toast.error(strings?.error_occurred_while_updating_description);
      return value;
    }
  };

  const tabsList = [
    {
      key: 0,
      label: 'Write',
      show: true,
      children: (
        <div className={styles.editorWrapper}>
          <Modal
            showActions={true}
            ModalBody={<VideoForm addVideo={addVideo} setVideoModal={setVideoModal} ref={videoFormRef} />}
            open={videoModal}
            title={strings?.add_video}
            okText={strings?.add}
            width={400}
            handleCancel={() => {
              setVideoModal(false);
            }}
            handleOk={() => {
              videoFormRef?.current?.handleSubmit();
            }}
            destroyOnClose={true}
          />
          <div>
            <NpmReactQuill
              ref={quillRef}
              style={{ '--quill-container-height': editorHeight }}
              linesCount="8"
              placeholder={placeholder}
              value={value}
              onChange={async (e, delta) => {
                const filteredUrls = delta
                  ?.filter((op) => {
                    const url = op?.insert?.image;
                    return (
                      url &&
                      !url?.includes(`/${tenant?.name?.toLowerCase()}/`) &&
                      !url?.includes('#') &&
                      url !== 'undefined'
                    );
                  })
                  ?.map((op) => op?.insert?.image);
                if (filteredUrls?.length && !!addProductConfig?.allow_image_in_description) {
                  setLoading(true);
                  let filePromises = [];
                  for (const url of filteredUrls) {
                    filePromises?.push(createFileFromString(url));
                  }
                  try {
                    const files = await Promise.allSettled(filePromises);
                    const updatedFiles = files
                      .filter((result) => result?.status === 'fulfilled')
                      .map((result) => result?.value);
                    const newHTML = await handlePaste(e, updatedFiles);
                    onChange(filterMultimedia(newHTML));
                  } catch (err) {
                    // Toast.error(strings?.error_occurred_while_updating_description);
                  } finally {
                    setLoading(false);
                  }
                } else {
                  onChange(filterMultimedia(e));
                }
              }}
              onBlur={onBlur}
              readOnly={loading || readOnly}
              modules={modules ? modules : defaultModules}
              theme={theme || 'snow'}
              className={className}
            />
            {loading && (
              <div className={styles.quillSpinner}>
                <Spin />
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 1,
      label: 'Preview',
      show: true,
      children: (
        <div
          className={cx(styles.preview, 'ql-editor')}
          style={{ padding: '10px 15px', minHeight: '200px' }}
          dangerouslySetInnerHTML={{ __html: value }}
        />
      ),
    },
  ];

  return (
    <>
      {label && <Label required={required}>{label}</Label>}
      <Tabs tabsList={tabsList} />
      <ErrorMessage errorMsg={errorMsg} />
    </>
  );
};

export default ReactQuill;
